import {
  useState,
  useEffect,
  useCallback
} from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
  Timestamp
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { pollPOS } from "../utils/posPolling.js";
import { serverNow } from "../utils/serverTime.js";
import { ABORT_TIMEOUT, finalPaymentCheck, getPaymentOutcome, waitForPayment } from "../utils/waitForPayment.js";
import { CART_ID_SOURCE, addPendingPayment, getPendingPayment, removePendingPayment, updatePendingPayment } from "../utils/pendingPayments.js";
import { classifyPaymentFailure } from "../utils/paymentFailure.js";

// KIO-03: respuestas de getCheckout en que no se sabe cómo terminó el cobro (la Cloud Function o el
// gateway cortaron la petición). El carro igual existe y el POS pudo aprobar: se busca el carro.
const UNKNOWN_CHECKOUT_HTTP_STATUSES = [408, 502, 503, 504];

// Margen hacia atrás al buscar el carro del cobro en curso, por si el reloj del dispositivo no
// está bien sincronizado con el del servidor.
const CART_LOOKUP_MARGIN_MS = 15000;

function deepEqual(obj1, obj2) {
  if (obj1 === obj2) return true;

  if (typeof obj1 !== 'object' || obj1 === null ||
    typeof obj2 !== 'object' || obj2 === null) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (let key of keys1) {
    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }
  return true;
}

export function useShoppingCart(env, client, storeId, instanceId, identifiedMember = null) {
  const [cart, setCart] = useState(null);
  const [cartId, setCartId] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const preparedDetailsToFirebase = ((details = []) => {
    const newDetails = details.map((d) => {
      const payload = {
        unique_id: d.id,
        id: d.productId,
        modifiers: d.selectedExtras,
        quantity: d.quantity,
      };
      if (d.appliedPromotions && d.appliedPromotions.length > 0) {
        // quantity = times the rule fired; units = units it discounted. They differ on
        // any promotion that covers more than one unit per activation, like a 2x1.
        payload.promotions = d.appliedPromotions.map(p => ({
          id: p.id,
          quantity: p.quantity,
          units: p.units,
          discount_amount: p.discount
        }));
      }
      return payload;
    })
    return newDetails;
  });

  const preparedDetailsFromFirebase = ((details = []) => {
    const newDetails = details.map((d) => {
      return {
        id: d.raw?.unique_id || d.unique_id,
        productId: d.raw?.id || d.id,
        selectedExtras: d.raw?.modifiers || d.modifiers,
        quantity: d.raw?.quantity || d.quantity,
      }
    })
    return newDetails;
  });

  const loadOrCreateCart = useCallback(async () => {
    setLoading(true);

    // let storedCartId = localStorage.getItem("kiosk_cart_id");
    let storedCartId = null; // FIX: Cargar un carro preexistente provoca que el checkout parta en un estado que no es el inicial.
    let cartRef;

    try {
      // If we already have a cart ID in localStorage → load it
      if (storedCartId) {
        cartRef = doc(db, `kiosk/${env}/client/${client}/carts/${storedCartId}`);
        const snap = await getDoc(cartRef);

        if (snap.exists()) {
          const cartData = snap.data();
          const validStatus = cartData.checkout_status !== 'processed' && cartData.checkout_status !== 'paid'; // Cuando se cierra la app o se recarga sin haber cerrado el modal de checkout en la vista de pago finalizado, el carro de compras pagado queda en el localstorage, esto hace que al cargar la página se dispare inmediatamente la impresión del documento que ya fue pagado. Para evitar esto, puse este fix que sólo se trae carros en donde el proceso no haya terminado correctamente.
          if (validStatus) {
            cartData.details = preparedDetailsFromFirebase(cartData.details)
            setCart(cartData);
            setCartId(cartData.id || storedCartId);
            setOrderId(cartData.external_order_id);
            setLoading(false);
            return;
          }
        }
      }

      // If no cart exists, create an empty cart in memory (not in Firestore)
      // This prevents infinite loading state
      const initialCart = {
        created_at: Timestamp.now(),
        updated_at: Timestamp.now(),
        store_id: storeId ?? null,
        details: [], // same structure as REST API
      };

      setCart(initialCart);
      setCartId(null);
      setOrderId(null);
      setLoading(false);
    } catch (error) {
      console.error("Error loading/creating cart:", error);
      // Set a fallback empty cart to prevent null state
      const fallbackCart = {
        created_at: Timestamp.now(),
        updated_at: Timestamp.now(),
        store_id: storeId ?? null,
        details: [],
      };
      setCart(fallbackCart);
      setLoading(false);
    }
  }, [env, client, storeId]);

  useEffect(() => {
    if (env && client) {
      loadOrCreateCart();
    }
  }, [env, client, loadOrCreateCart]);

  const updateCart = useCallback(
    async (newDetails) => {
      if (!cartId) {
        const newCart = {...cart, details: newDetails, updated_at: Timestamp.now()};
        setCart(newCart);
        return;
      }
      const cartDocRef = doc(db, `kiosk/${env}/client/${client}/carts/${cartId}`);
      const newCart = {...cart, details: preparedDetailsToFirebase(newDetails), updated_at: Timestamp.now()};
      await updateDoc(cartDocRef, newCart);
      newCart.details = preparedDetailsFromFirebase(newCart.details);
      setCart(newCart);
    },
    [cart, cartId, env, client]
  );

  const addItem = (item) => {
    const previous_details = cart?.details || [];
    const updated = [...previous_details, item];
    updateCart(updated);
  };

  const removeItem = (id) => {
    const previous_details = cart?.details || [];
    const updated = previous_details.filter((d) => d.id !== id);
    updateCart(updated);
  };

  const updateQuantity = (id, quantity) => {
    const previous_details = cart?.details || [];
    const updated = previous_details.map((d) =>
      d.id === id ? { ...d, quantity } : d
    );
    updateCart(updated);
  };

  /**
   * Actualiza un item existente (cantidad y/o modificadores). Si al cambiar los
   * modificadores el item queda idéntico a otra línea del carro, ambas se fusionan.
   */
  const updateItem = (id, changes) => {
    const previous_details = cart?.details || [];
    const target = previous_details.find((d) => d.id === id);
    if (!target) return;

    const updatedItem = {
      ...target,
      quantity: changes.quantity !== undefined ? changes.quantity : target.quantity,
      selectedExtras: changes.selectedExtras !== undefined ? changes.selectedExtras : target.selectedExtras,
    };

    const twin = previous_details.find(
      (d) => d.id !== id && d.productId === updatedItem.productId && deepEqual(d.selectedExtras, updatedItem.selectedExtras)
    );

    let updated;
    if (twin) {
      updated = previous_details
        .filter((d) => d.id !== id)
        .map((d) => (d.id === twin.id ? { ...d, quantity: d.quantity + updatedItem.quantity } : d));
    } else {
      updated = previous_details.map((d) => (d.id === id ? updatedItem : d));
    }

    updateCart(updated);
  };

  const addOrUpdateItem = (item) => {
    const previous_details = cart?.details || [];
    let found = false;
    const updated = previous_details.map((d) => {
      if (d.productId === item.productId && deepEqual(d.selectedExtras, item.selectedExtras) && !found) {
        found = true;
        return { ...d, quantity: (d.quantity + item.quantity), id: item.id }
      } else {
        return d
      }
    });
    if (!found) updated.push(item);
    updateCart(updated);
  };

  const newCart = async () => {
    setCart(null);
    setCartId(null);
    setOrderId(null);
    setOrderStatus(null);
    setLoading(false);
    localStorage.removeItem("kiosk_cart_id");
    await loadOrCreateCart();
  }

  const clearCart = async () => {
    await updateCart([]);
  };

  const getTotals = (enrichedItems, getProduct, getProductModifiers, getProductInfo, getExtras, filteredStoreIds) => {
    const { normalTotal, commonTotal } = enrichedItems.reduce((acc, item) => {
      const product = getProduct(item.productId);
      if (!product) return acc;
      const productModifiers = getProductModifiers(product.modifiers || [])
      const { normalPrice, commonPrice } = getProductInfo(product, filteredStoreIds);
      const extras = getExtras(product, productModifiers, item.selectedExtras);
      const extraSubtotal = extras.reduce((sum, ex) => sum += (ex.price * ex.quantity), 0)
      const itemNormalTotal = (normalPrice + extraSubtotal) * item.quantity;
      const itemCommonTotal = ((commonPrice + extraSubtotal) * item.quantity) - (item.promoDiscount || 0);
      return {
        normalTotal: acc.normalTotal + itemNormalTotal,
        commonTotal: acc.commonTotal + itemCommonTotal,
      };
    }, { normalTotal: 0, commonTotal: 0 });
    return { normalTotal, commonTotal };
  };

  const pollingViaAPI = async (signal, type, automatic = false) => {
    return await pollPOS({ env, client, instanceId, storeId, type, signal, automatic });
  };

  const checkoutViaAPI = async (paymentOption = null, enrichedDetails = null, externalSaleFlow = false, signal = null) => {
    if (!cart || cart.details.length === 0) {
      throw new Error("Cart is empty");
    }

    const url = `https://getcheckout-x3herayjcq-uc.a.run.app?environment=${env}&client=${client}`;

    const body = {
      details: enrichedDetails ? preparedDetailsToFirebase(enrichedDetails) : preparedDetailsToFirebase(cart.details),
      store_id: storeId,
      instance_id: instanceId,
      external_sale_flow: externalSaleFlow,
      is_client: false,
      payment_option: paymentOption,
      // Canal de venta del cart (contrato BE-19): el tótem siempre es "kiosk".
      source: "kiosk",
    };

    // Only sent when the shopper identified: the backend stores it on the cart, which is
    // what ties the sale — and the promotions it used — back to the member.
    if (identifiedMember) {
      body.loyalty = identifiedMember;
    }

    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Checkout API failed");
      console.error(errorText);
      return {status: false, data: errorText, httpStatus: response.status};
    }

    const data = await response.json();

    data.better_details = data.details;
    data.details = preparedDetailsFromFirebase(data.details);

    setCartId(data.id);
    setOrderId(data.external_order_id);
    setOrderStatus(data.checkout_status);
    setCart(data);

    localStorage.setItem("kiosk_cart_id", data.id);

    return {status: externalSaleFlow ? !!data.id : data.processed_sale, data: data};
  };

  const checkoutViaUSB = async (commonTotal, ticket) => {
    try {
      if (!ticket) throw new Error("Invalid ticket");
      if (!commonTotal) throw new Error("Invalid total amount");
      if (!cart || cart.details.length === 0) throw new Error("Cart is empty");
      const url = `https://localhost:5001/sale`;
      const amount = commonTotal;
      const body = {Amount: amount, Ticket: ticket.toString(), Print: 0};
      const response = await fetch(url, {method: 'POST', mode: 'cors', cache: 'no-cache', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(body)})
      const data = await response.json();
      return {status: data && data.responseCode === 0, data: data};
    } catch (error) {
      return {status: false, data: {error: error.message || "Failed to connect to POS system"}};
    }
  };

  // checkCheckoutStatus sin tocar el estado. La usa checkOrderStatus y la recuperación de cobros
  // pendientes (SOK-04), que corre con otro cliente frente al tótem y no puede cambiarle el carro.
  const fetchCheckoutStatus = async (idToUse, signal = null) => {
    const url = `https://checkcheckoutstatus-x3herayjcq-uc.a.run.app?environment=${env}&client=${client}&cart_id=${idToUse}`;

    const response = await fetch(url, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      signal,
    });

    if (!response.ok) {
      console.error(await response.text());
      throw new Error("Check Checkout API failed");
    }

    const data = await response.json();

    data.better_details = data.details;
    data.details = preparedDetailsFromFirebase(data.details);
    return data;
  };

  const checkOrderStatus = async (providedCartId = null, signal = null) => {
    // Use provided cartId or fall back to state cartId
    const idToUse = providedCartId || cartId;

    if (!idToUse) {
      throw new Error("Cart ID is required");
    }

    const data = await fetchCheckoutStatus(idToUse, signal);

    // Un carro pendiente no se carga al estado, igual que en el camino de Firestore (KIO-03): el polling
    // usa lo que devuelve esta función, y un 'created' cargado (el expired de Mercado Pago antes de
    // BE-04) llegaba al modal de cobro y lo reiniciaba.
    if (getPaymentOutcome(data) === "pending") return data;

    setCartId(data.id);
    setOrderId(data.external_order_id);
    setOrderStatus(data.checkout_status);
    setCart(data);

    localStorage.setItem("kiosk_cart_id", data.id);

    return data; // return checkout result
  };

  // KIO-03: carro creado por getCheckout para este cobro, cuando getCheckout no alcanzó a responder.
  // El backend lo crea antes de cobrar, así que existe aunque la petición se haya cortado. Se
  // descarta el carro anterior del tótem para no confundirlo con una compra previa.
  const findPaymentCartId = async (startedAt, previousCartId) => {
    const since = Timestamp.fromMillis(startedAt.getTime() - CART_LOOKUP_MARGIN_MS);
    const carts = collection(db, `kiosk/${env}/client/${client}/carts`);
    const snapshot = await getDocs(query(carts, where("created_at", ">=", since)));
    const candidates = snapshot.docs
      .filter((d) => d.id !== previousCartId && d.data().instance_id === instanceId)
      .sort((a, b) => b.data().created_at.toMillis() - a.data().created_at.toMillis());
    return candidates[0]?.id || null;
  };

  const readCart = async (id) => {
    const snapshot = await getDoc(doc(db, `kiosk/${env}/client/${client}/carts/${id}`));
    return snapshot.exists() ? { id, ...snapshot.data() } : null;
  };

  // KIO-03: con Getnet la venta es síncrona: getCheckout queda abierto mientras el cliente paga en
  // el POS (hasta 90 s). Si responde pendiente, se consulta checkCheckoutStatus hasta que el modal
  // aborte el cobro. Si no responde (abortado, red caída, función cortada), se lee el carro en
  // Firestore, donde el backend deja el resultado cuando el POS contesta.
  // SOK-04: el cobro queda anotado (utils/pendingPayments.js) hasta que se sabe cómo terminó. Si
  // termina en 'timeout' o 'aborted' el registro queda, y la respuesta lleva su id para reanudar.
  const checkoutViaPOSAPI = async (paymentOption, signal, onFinalCheck) => {
    const startedAt = serverNow();
    const previousCartId = localStorage.getItem("kiosk_cart_id");
    const pending = addPendingPayment({ startedAt, method: paymentOption?.type_details || null, previousCartId });
    const settle = (result) => {
      if (result.outcome === "paid" || result.outcome === "canceled" || result.outcome === "failed") removePendingPayment(pending.id);
      return result.outcome === "timeout" || result.outcome === "aborted" ? { ...result, pendingPaymentId: pending.id } : result;
    };

    let response = null;
    try {
      response = await checkoutViaAPI(paymentOption, false, false, signal);
    } catch (error) {
      if (signal?.aborted && signal.reason !== ABORT_TIMEOUT) return settle({ status: false, outcome: "aborted", data: null });
      console.warn("getCheckout no respondió, se busca el carro del cobro:", error);
    }

    if (response && !UNKNOWN_CHECKOUT_HTTP_STATUSES.includes(response.httpStatus)) {
      // Error del backend con mensaje (p. ej. el POS rechazó la venta): se muestra como hasta ahora.
      if (typeof response.data !== "object" || !response.data) {
        settle({ outcome: "failed" });
        return response;
      }
      const id = response.data.id;
      if (id) updatePendingPayment(pending.id, { cart_id: id, cart_id_source: CART_ID_SOURCE.CHECKOUT });
      const outcome = getPaymentOutcome(response.data);
      if (outcome !== "pending") return settle({ ...response, status: outcome === "paid", outcome });

      const result = await waitForPayment({
        check: (requestSignal) => checkOrderStatus(id, requestSignal),
        signal,
        lastData: response.data,
        onFinalCheck,
      });
      return settle({ status: result.outcome === "paid", outcome: result.outcome, data: result.data });
    }

    // No se sabe cómo terminó getCheckout. Solo se lee Firestore: checkCheckoutStatus sin el
    // payment_id de Getnet podría dar por cancelado un cobro que sigue en el POS (BE-04).
    let foundId = null;
    const check = async () => {
      if (!foundId) {
        foundId = await findPaymentCartId(startedAt, previousCartId);
        if (foundId) updatePendingPayment(pending.id, { cart_id: foundId, cart_id_source: CART_ID_SOURCE.FIRESTORE });
      }
      return foundId ? await readCart(foundId) : null;
    };
    const result = await waitForPayment({ check, signal, onFinalCheck });
    const settled = settle({ status: result.outcome === "paid", outcome: result.outcome, data: result.data });
    // Un carro pendiente no se carga al estado (ver checkOrderStatus).
    if (!foundId || (result.outcome !== "paid" && result.outcome !== "canceled")) return settled;

    // Se carga el carro con checkOrderStatus (ya tiene el payment_id), para que el modal vea el
    // estado final con los mismos datos que en el camino normal; si falla, con lo leído en Firestore.
    try {
      return { ...settled, data: await checkOrderStatus(foundId) };
    } catch (error) {
      console.warn("No se pudo cargar el carro del cobro, se usa el de Firestore:", error);
      setCartId(foundId);
      setOrderStatus(result.data.checkout_status);
      setCart((prev) => ({ ...prev, ...result.data, details: prev?.details || [] }));
      localStorage.setItem("kiosk_cart_id", foundId);
      return settled;
    }
  };

  // SOK-04: carro y forma de consultarlo para un cobro anotado. Con el cart_id de getCheckout se usa
  // checkCheckoutStatus (el backend vuelve a preguntarle al proveedor); sin él, solo Firestore.
  const pendingPaymentCheck = async (record, { loadState }) => {
    let id = record.cart_id;
    let source = record.cart_id_source;
    if (!id) {
      id = await findPaymentCartId(new Date(record.started_at), record.previous_cart_id);
      source = CART_ID_SOURCE.FIRESTORE;
    }
    if (!id) return { id: null, check: async () => null };
    if (source === CART_ID_SOURCE.CHECKOUT) {
      return { id, check: (requestSignal) => (loadState ? checkOrderStatus(id, requestSignal) : fetchCheckoutStatus(id, requestSignal)) };
    }
    return { id, check: () => readCart(id) };
  };

  /**
   * SOK-04 (punto 4): reanuda un cobro cortado por la red, con la misma consulta final que KIO-03.
   * Nunca cobra de nuevo. Si terminó, carga el carro al estado (el modal pasa a imprimir o al fallo,
   * como en el camino normal) y borra el registro.
   *
   * @param {string} pendingPaymentId
   * @returns {Promise<{status: boolean, outcome: 'paid'|'canceled'|'timeout', data: object | null, pendingPaymentId?: string}>}
   */
  const resumePendingPayment = async (pendingPaymentId) => {
    const record = getPendingPayment(pendingPaymentId);
    if (!record) return { status: false, outcome: "timeout", data: null };
    const { id, check } = await pendingPaymentCheck(record, { loadState: true });
    if (!id) return { status: false, outcome: "timeout", data: null, pendingPaymentId };
    if (!record.cart_id) updatePendingPayment(record.id, { cart_id: id, cart_id_source: CART_ID_SOURCE.FIRESTORE });

    const result = await finalPaymentCheck({ check });
    if (result.outcome !== "paid" && result.outcome !== "canceled") return { status: false, outcome: "timeout", data: result.data, pendingPaymentId };
    removePendingPayment(record.id);
    // Leído de Firestore: se carga con checkOrderStatus, igual que el camino de KIO-03.
    let data = result.data;
    if (record.cart_id_source !== CART_ID_SOURCE.CHECKOUT) {
      try {
        data = await checkOrderStatus(id);
      } catch (error) {
        console.warn("No se pudo cargar el carro del cobro reanudado, se usa el de Firestore:", error);
        setCartId(id);
        setOrderStatus(result.data.checkout_status);
        setCart((prev) => ({ ...prev, ...result.data, details: prev?.details || [] }));
      }
    }
    return { status: result.outcome === "paid", outcome: result.outcome, data };
  };

  /**
   * SOK-04 (punto 5): consulta un cobro anotado sin tocar el estado (puede haber otro cliente
   * comprando). Una sola consulta; si no responde, rechaza y el registro queda para la próxima.
   *
   * @returns {Promise<{outcome: 'paid'|'canceled'|'pending', cartId: string | null, data: object | null}>}
   */
  const checkPendingPaymentInBackground = async (record, signal = null) => {
    const { id, check } = await pendingPaymentCheck(record, { loadState: false });
    if (!id) return { outcome: "pending", cartId: null, data: null };
    const data = await check(signal);
    return { outcome: getPaymentOutcome(data), cartId: id, data };
  };

  /**
   * @param {object} [options]
   * @param {AbortSignal} [options.signal] - KIO-03: el modal lo aborta al vencer la espera + la
   *   holgura (ABORT_TIMEOUT) o al cerrarse (ABORT_CLOSED). Solo lo usa el cobro POSAPI.
   * @param {() => void} [options.onFinalCheck] - Avisa que empezó la consulta final.
   * @returns {Promise<{status: boolean, data: any, outcome?: 'paid'|'canceled'|'timeout'|'aborted'}>}
   */
  const checkoutAndWaitStatusViaAPI = async (paymentOption = null, enrichedDetails = null, commonTotal = null, clientData = null, instance = null, _products = [], store = null, { signal = null, onFinalCheck = null } = {}) => {
    let data = null;
    if (!paymentOption) return data;
    if (paymentOption.type === 'POS') {
      data = await checkoutViaAPI(paymentOption, enrichedDetails, true);

      const id = data?.data?.id;
      const numericId = data?.data?.numeric_id;
      const betterDetails = data?.data?.better_details;
      
      data = await checkoutViaUSB(commonTotal, numericId);

      cart.payment_id = numericId;
      cart.payment_summary = data?.data || null;
      cart.external_order_id = numericId;
      cart.checkout_summary = data?.data || null;
      cart.checkout_status = data && data.status ? 'processed' : 'canceled';
      cart.processed_sale = data && data.status ? true : false;
      cart.checkout_was_printed = false;
      cart.checkout_terminal = instance?.terminal_data || null;
      cart.updated_at = Timestamp.now();

      const amount = data && data.data && data.data.amount ? data.data.amount : null;

      cart.ticket_data = {
        aid: data.data?.authorizationCode || null,
        amount: amount,
        branch_address: store?.address || null,
        branch_district: null,
        branch_name: store?.name || null,
        card_brand: data.data?.cardBrand || null,
        card_type: data.data?.cardType || null,
        commerce_name: clientData?.name || null,
        commerce_tin: clientData?.pin || null,
        iva_amount: amount && clientData && clientData.tax ? Math.round(amount * clientData.tax) : null,
        last_digits: data.data?.last4Digits || null,
        operation_id: data.data?.ticket || null,
        terminal_id: data.data?.terminalId || null,
        ticket_date: Timestamp.now()
      };

      cart.better_details = betterDetails;

      setCart(cart);

      const newData = {
        payment_id: numericId,
        payment_summary: data?.data || null,
        external_order_id: numericId,
        checkout_summary: data?.data || null,
        checkout_status: data && data.status ? 'processed' : 'canceled',
        processed_sale: data && data.status ? true : false,
        checkout_was_printed: false,
        checkout_terminal: instance?.terminal_data || null,
        updated_at: Timestamp.now()
      };
      // SOK-02: motivo del rechazo, el mismo que muestra el modal.
      if (!data || !data.status) newData.payment_failure_reason = classifyPaymentFailure(data).reason;

      if (id) {
        const reference = doc(db, `kiosk/${env}/client/${client}/carts/${id}`);
        await updateDoc(reference, newData);
      }
    } else if (paymentOption.type === 'POSAPI') {
      return await checkoutViaPOSAPI(paymentOption, signal, onFinalCheck);
    }
    return data;
  };

  return {
    cart,
    cartId,
    orderId,
    orderStatus,
    loading,

    // Cart operations
    //*
    addItem,
    removeItem,
    updateQuantity,
    updateItem,
    addOrUpdateItem,
    newCart,
    clearCart,
    setCart,
    setCartId,
    setOrderId,
    setOrderStatus,
    setLoading,
    getTotals,
    pollingViaAPI,
    checkoutViaAPI,
    checkOrderStatus,
    checkoutAndWaitStatusViaAPI,
    resumePendingPayment,
    checkPendingPaymentInBackground,
  };
}